import { computed, ref, type Ref } from 'vue';

import type { DocumentRevision } from '@/entities/Document';

import {
  selectLocalFiles,
  selectLocalUrls,
  type ElectronicSendMode,
  type LocalUrl,
} from './electronic-attachments';

export interface SelectedGdDocument {
  id: number;
  prefix: string;
  title: string | null;
  revision: number;
  originalFileName: string;
  fileSizeBytes: number;
}

export const DOCUMENT_SEND_MAX_GD_DOCUMENTS = 10;
const TOTAL_ATTACHMENTS_EXCEEDED_MESSAGE = 'O número máximo total de anexos por envio é 10.';

const TOTAL_SIZE_EXCEEDED_MESSAGE =
  'O total de anexos ultrapassa 10MB. Remova arquivos para continuar.';

export const parseFileSize = (value: DocumentRevision['fileSize']): number => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return Math.max(value, 0);
  }

  if (typeof value === 'string') {
    const parsed = Number.parseInt(value, 10);
    return Number.isFinite(parsed) ? Math.max(parsed, 0) : 0;
  }

  return 0;
};

const normalizeGdDocument = (revision: DocumentRevision): SelectedGdDocument => {
  const prefix = revision.document?.prefix ?? '';
  const originalFileName =
    revision.originalFileName?.trim() || `${prefix || 'documento'}_${revision.id}.pdf`;

  return {
    id: revision.id,
    prefix,
    title: revision.document?.name ?? null,
    revision: revision.revision,
    originalFileName,
    fileSizeBytes: parseFileSize(revision.fileSize),
  };
};

export function useGdDocumentSelection(params: {
  documentSelectionError: Ref<string>;
  localFiles: Ref<File[]>;
  localUrls: Ref<LocalUrl[]>;
  selectedElectronicMode: Ref<ElectronicSendMode>;
  maxAttachmentCount: number;
  maxAttachmentBytes: number;
}) {
  const selectedGdDocuments = ref<SelectedGdDocument[]>([]);
  const gdDocumentRevisionIds = computed(() =>
    selectedGdDocuments.value.map((document) => document.id)
  );
  const gdDocumentsTotalBytes = computed(() =>
    selectedGdDocuments.value.reduce((total, document) => total + document.fileSizeBytes, 0)
  );
  const activeLocalFiles = computed(() =>
    selectLocalFiles(params.selectedElectronicMode.value, params.localFiles.value)
  );
  const activeLocalUrlCount = computed(
    () => selectLocalUrls(params.selectedElectronicMode.value, params.localUrls.value).length
  );
  const localFilesTotalBytes = computed(() =>
    activeLocalFiles.value.reduce((total, file) => total + file.size, 0)
  );
  const totalAttachmentCount = computed(
    () =>
      selectedGdDocuments.value.length + activeLocalFiles.value.length + activeLocalUrlCount.value
  );
  const totalAttachmentBytes = computed(
    () => gdDocumentsTotalBytes.value + localFilesTotalBytes.value
  );

  const replaceSelectedGdDocuments = (revisions: DocumentRevision[]): boolean => {
    params.documentSelectionError.value = '';

    const uniqueRevisions = revisions.filter(
      (revision, index, items) => items.findIndex((item) => item.id === revision.id) === index
    );

    if (uniqueRevisions.length > DOCUMENT_SEND_MAX_GD_DOCUMENTS) {
      params.documentSelectionError.value = 'O número máximo de documentos do GD por envio é 10.';
      return false;
    }

    if (
      uniqueRevisions.length + activeLocalFiles.value.length + activeLocalUrlCount.value >
      params.maxAttachmentCount
    ) {
      params.documentSelectionError.value = TOTAL_ATTACHMENTS_EXCEEDED_MESSAGE;
      return false;
    }

    const normalizedDocuments = uniqueRevisions.map(normalizeGdDocument);
    const nextTotalAttachmentBytes =
      normalizedDocuments.reduce((total, document) => total + document.fileSizeBytes, 0) +
      localFilesTotalBytes.value;

    if (nextTotalAttachmentBytes > params.maxAttachmentBytes) {
      params.documentSelectionError.value = TOTAL_SIZE_EXCEEDED_MESSAGE;
      return false;
    }

    selectedGdDocuments.value = normalizedDocuments;
    return true;
  };

  const addSelectedGdDocument = (revision: DocumentRevision): void => {
    params.documentSelectionError.value = '';

    if (selectedGdDocuments.value.some((document) => document.id === revision.id)) {
      params.documentSelectionError.value = 'Este documento GD já foi selecionado.';
      return;
    }

    if (selectedGdDocuments.value.length >= DOCUMENT_SEND_MAX_GD_DOCUMENTS) {
      params.documentSelectionError.value = 'O número máximo de documentos do GD por envio é 10.';
      return;
    }

    if (totalAttachmentCount.value >= params.maxAttachmentCount) {
      params.documentSelectionError.value = TOTAL_ATTACHMENTS_EXCEEDED_MESSAGE;
      return;
    }

    const normalizedDocument = normalizeGdDocument(revision);

    if (totalAttachmentBytes.value + normalizedDocument.fileSizeBytes > params.maxAttachmentBytes) {
      params.documentSelectionError.value = TOTAL_SIZE_EXCEEDED_MESSAGE;
      return;
    }

    selectedGdDocuments.value = [...selectedGdDocuments.value, normalizedDocument];
  };

  const removeSelectedGdDocument = (revisionId: number): void => {
    selectedGdDocuments.value = selectedGdDocuments.value.filter(
      (document) => document.id !== revisionId
    );
    params.documentSelectionError.value = '';
  };

  return {
    addSelectedGdDocument,
    gdDocumentRevisionIds,
    gdDocumentsTotalBytes,
    removeSelectedGdDocument,
    replaceSelectedGdDocuments,
    selectedGdDocuments,
    totalAttachmentBytes,
  };
}
